import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Trash2, X } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>
interface Props { item: ContentItem; onSave: (payload: Payload) => void; saving?: boolean }

// Review/curate the graph-generated "swim lanes". Curators can tweak the label/blurb,
// drop weak handles, or remove a whole lane before approving (publish writes the
// artwork_dimensions metaobject). Lanes themselves are generated, not hand-authored.
export function RecommendationEditor({ item, onSave, saving }: Props) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => { setP((item.payload as Payload) || {}) }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const lanes: Payload[] = Array.isArray(p.lanes) ? p.lanes : []
  const setLanes = (next: Payload[]) => setP((prev) => ({ ...prev, lanes: next }))
  const updateLane = (i: number, k: string, v: any) => setLanes(lanes.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)))
  const removeLane = (i: number) => setLanes(lanes.filter((_, idx) => idx !== i))
  const removeHandle = (li: number, h: string) =>
    updateLane(li, 'handles', (lanes[li].handles || []).filter((x: string) => x !== h))

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">
        Seeded from <span className="font-medium">{item.targetRef}</span> · {lanes.length} lane{lanes.length === 1 ? '' : 's'}
        {Array.isArray(p.shared_concepts) && p.shared_concepts.length ? ` · ${p.shared_concepts.join(', ')}` : ''}
      </p>

      {lanes.map((l, i) => (
        <div key={i} className="rounded-md border p-4 space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
            <Field label="Lane label"><Input value={String(l.label || '')} onChange={(e) => updateLane(i, 'label', e.target.value)} /></Field>
            <Field label="Kind"><Input className="w-32" value={String(l.kind || '')} onChange={(e) => updateLane(i, 'kind', e.target.value)} /></Field>
          </div>
          <Field label="Blurb"><Textarea rows={2} value={String(l.blurb || '')} onChange={(e) => updateLane(i, 'blurb', e.target.value)} /></Field>
          <div className="space-y-1.5">
            <Label>Artworks ({(l.handles || []).length})</Label>
            <div className="flex flex-wrap gap-1.5">
              {(l.handles || []).map((h: string) => (
                <span key={h} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs">
                  {h}
                  <button type="button" className="text-muted-foreground hover:text-red-600" onClick={() => removeHandle(i, h)}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div className="flex">
            <Button variant="ghost" size="sm" className="text-red-600 ml-auto" onClick={() => removeLane(i)}>
              <Trash2 className="h-4 w-4 mr-1" /> Remove lane
            </Button>
          </div>
        </div>
      ))}
      {lanes.length === 0 && <p className="text-sm text-muted-foreground">No lanes generated — regenerate from the source artwork.</p>}

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
